import { DepartmentModel, NationalBankRate } from '@/interface';
import { getRateDivisor } from '@/core/helpers/PageHelpers';

/** Курсы для пересчёта остатков в тенге */
export interface CapitalRates {
    /** Курсы НБ РК (`/NB/xrates` → rates.item) */
    nbRates: NationalBankRate[];
    /** Цена грамма золота НБ РК; 0 — не загрузилась */
    goldPrice: number;
}

export interface CapitalDepartment {
    department: DepartmentModel;
    /** Остаток точки в тенге */
    balance: number;
    /** Сколько уставного капитала должна держать точка */
    required: number;
}

/** Юрлицо: точки с одинаковым name (ECASH и его Астанинский филиал — одно юрлицо) */
export interface CapitalGroup {
    key: string;
    /** Все name, попавшие в группу */
    names: string[];
    departments: CapitalDepartment[];
    /** Уставной капитал юрлица — сумма остатков всех его точек */
    balance: number;
    required: number;
    /** На сколько не хватает; 0 — хватает */
    shortage: number;
}

const BIG_CITY_CAPITAL = 100_000_000;
const DEFAULT_CAPITAL = 50_000_000;
const BIG_CITIES = ["Алматы", "Астана"];

/** Названия, которые считаются одним юрлицом с первым в списке */
const LINKED_NAMES: Record<string, string> = {
    "ECASH Астанинский филиал": "ECASH",
};

export const getCapitalGroupKey = (name?: string) => {
    const trimmed = (name || "").trim();
    return LINKED_NAMES[trimmed] ?? trimmed;
};

export const getRequiredCapital = (dep: DepartmentModel) => {
    const region = (dep?.region || dep?.city || "").trim();
    return BIG_CITIES.includes(region) ? BIG_CITY_CAPITAL : DEFAULT_CAPITAL;
};

/** Цена золота НБ РК приходит строкой с пробелами-разделителями: «45 123,45» */
export const parseGoldPrice = (price?: string | number) => +(`${price ?? ""}`.replace(/\s/g, "").replace(",", ".")) || 0;

/**
 * Остаток точки в тенге — так же, как «Общий баланс в тг» на странице казначея:
 * активные валюты по курсу НБ РК (нет курса — по нашей покупке), OMR не учитывается,
 * золото по цене грамма НБ РК × граммы из кода, плюс тенге. Резерв не вычитается.
 */
export const getDepartmentKztBalance = (dep: DepartmentModel, { nbRates, goldPrice }: CapitalRates) => {
    const balanceOf = (code: string) => dep?.balanceList?.find(b => b.currencyCode === code)?.balance ?? 0;

    let total = balanceOf("KZT");

    dep?.currencies?.forEach(currency => {
        const code = currency?.code || "";
        const buy = currency?.rates?.[0]?.buy || 0;

        if (code === "KZT") return;

        if (/GOLD/i.test(code)) {
            total += Math.round(balanceOf(code) * (goldPrice || buy) * +code.replace(/GOLD/i, ""));
            return;
        }

        if (!currency.isActive || code === "OMR") return;

        const nbRate = +(nbRates?.find(n => n?.title === code)?.description || 0);
        total += Math.round(balanceOf(code) * (nbRate || buy) / getRateDivisor(code));
    });

    return total;
};

/** Юрлица без недостачи, которые всегда идут в конце списка — в этом порядке */
const LAST_GROUPS = ["ECASH Kazakhstan", "Tandau Exchange", "E-Exchange"];

const lastGroupIndex = (group: CapitalGroup) => (group.shortage > 0 ? -1 : LAST_GROUPS.indexOf(group.key));

/**
 * Уставной капитал по юрлицам: сначала с недостачей (по убыванию недостачи),
 * потом остальные по алфавиту, в самом конце — LAST_GROUPS
 */
export const calcCharterCapital = (departments: DepartmentModel[], rates: CapitalRates): CapitalGroup[] => {
    const groups = new Map<string, CapitalGroup>();

    departments?.forEach(department => {
        const key = getCapitalGroupKey(department?.name);
        const group = groups.get(key) ?? { key, names: [], departments: [], balance: 0, required: 0, shortage: 0 };
        const balance = getDepartmentKztBalance(department, rates);
        const required = getRequiredCapital(department);
        const name = (department?.name || "").trim();

        if (!group.names.includes(name)) group.names.push(name);
        group.departments.push({ department, balance, required });
        group.balance += balance;
        group.required += required;
        groups.set(key, group);
    });

    return Array.from(groups.values())
        .map(group => ({ ...group, shortage: Math.max(group.required - group.balance, 0) }))
        .sort((a, b) => b.shortage - a.shortage
            || lastGroupIndex(a) - lastGroupIndex(b)
            || a.key.localeCompare(b.key));
};
